import React, { useEffect, useRef, useState } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ProgressBar } from './components/ProgressBar';
import { QuestionCard } from './components/QuestionCard';
import { SuccessScreen } from './components/SuccessScreen';
import { ClosedScreen } from './components/ClosedScreen';
import { DevTools } from './components/DevTools';
import { useSurveyState } from './hooks/useSurveyState';
import { QUESTIONS } from './constants/questions/index';
import { SURVEY_CONFIG } from './config/surveyConfig';
import { getNextQuestionIndex, getPreviousQuestionIndex, calculateRemainingQuestions } from './utils/questionFlow';
import { flattenAnswers } from './utils/transformAnswers';
import { submitSurveyWithRetry } from './services/surveyApi';
import { isDevelopment } from './utils/mockData';
import { initAnalytics, trackSurveySubmit } from './utils/analytics';

export default function App() {
  const questionRef = useRef(null);

  // Survey state
  const {
    currentStep,
    setCurrentStep,
    answers,
    setAnswers,
    error,
    setError,
    submitted,
    setSubmitted,
    navigationHistory,
    setNavigationHistory
  } = useSurveyState();

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  // Dev mode state for viewing status screens
  const [devViewStatus, setDevViewStatus] = useState(null); // 'closed' or 'waiting'

  const currentQuestion = QUESTIONS[currentStep];

  // Initialize analytics on mount
  useEffect(() => {
    initAnalytics();
  }, []);

  // Scroll to top when question changes
  useEffect(() => {
    if (questionRef.current) {
      // Scroll to the question card with smooth behavior
      questionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Fallback: scroll to top of page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [currentStep]);

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
    setError('');
  };

  const handleNext = () => {
    const validation = currentQuestion.validation(answers[currentQuestion.id]);
    if (validation) {
      setError(validation);
      return;
    }

    // Get next question index based on conditional logic
    const nextIndex = getNextQuestionIndex(
      currentStep,
      answers[currentQuestion.id],
      answers
    );

    if (nextIndex !== -1 && nextIndex < QUESTIONS.length) {
      setCurrentStep(nextIndex);
      setNavigationHistory([...navigationHistory, nextIndex]);
      setError('');
    }
  };

  const handleBack = () => {
    if (navigationHistory.length > 1) {
      const prevIndex = getPreviousQuestionIndex(navigationHistory);
      setCurrentStep(prevIndex);
      setNavigationHistory(navigationHistory.slice(0, -1));
      setError('');
    }
  };

  const handleSubmit = async () => {
    // Validate current question
    const validation = currentQuestion.validation(answers[currentQuestion.id]);
    if (validation) {
      setError(validation);
      return;
    }

    // Clear any previous errors
    setSubmissionError(null);
    setIsSubmitting(true);

    try {
      // Transform complex answers to simple key:value pairs for database
      const flattenedAnswers = flattenAnswers(answers, QUESTIONS);

      console.log('Respostas da pesquisa (original):', answers);
      console.log('Respostas da pesquisa (flattened for DB):', flattenedAnswers);

      // Submit to WordPress API with automatic retry
      const result = await submitSurveyWithRetry(flattenedAnswers, 2);

      console.log('Survey submitted successfully!', result);

      // Clear local storage on successful submission
      localStorage.removeItem('survey-progress');

      // Track survey submission
      trackSurveySubmit();

      // Show success screen
      setSubmitted(true);

    } catch (error) {
      // Handle submission errors
      console.error('Survey submission failed:', error);
      setSubmissionError(error.message);
      setIsSubmitting(false);

      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Dev tools handlers
  const handleFillMockData = (mockAnswers) => {
    setAnswers(mockAnswers);
    setError('');
  };

  const handleReset = () => {
    setAnswers({});
    setCurrentStep(0);
    setNavigationHistory([0]);
    setError('');
    setSubmitted(false);
    localStorage.removeItem('survey-progress');
  };

  const handleJumpToQuestion = (questionIndex) => {
    setCurrentStep(questionIndex);
    setNavigationHistory([questionIndex]);
    setError('');
    // Scroll will happen automatically via useEffect watching currentStep
  };

  const handleShowSuccess = () => {
    setSubmitted(true);
  };

  const handleShowClosedScreen = () => {
    setDevViewStatus('closed');
  };

  const handleShowWaitingScreen = () => {
    setDevViewStatus('waiting');
  };

  const handleBackToSurvey = () => {
    setDevViewStatus(null);
    setSubmitted(false);
  };

  // Check if current question is the last one
  const nextQuestionIndex = getNextQuestionIndex(
    currentStep,
    answers[currentQuestion.id],
    answers
  );
  const isLastQuestion = nextQuestionIndex === -1 || nextQuestionIndex >= QUESTIONS.length;

  // Calculate remaining questions for dynamic progress bar
  const { remaining: remainingQuestions, isEstimate } = calculateRemainingQuestions(
    currentStep,
    answers,
    QUESTIONS
  );

  // Dev mode: Show status screens if requested
  if (isDevelopment() && devViewStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
        <Header />
        <ClosedScreen status={devViewStatus} />
        <Footer />
        <DevTools
          onFillMockData={handleFillMockData}
          onReset={handleReset}
          onJumpToQuestion={handleJumpToQuestion}
          onShowSuccess={handleShowSuccess}
          onShowClosedScreen={handleShowClosedScreen}
          onShowWaitingScreen={handleShowWaitingScreen}
          onBackToSurvey={handleBackToSurvey}
          questions={QUESTIONS}
          currentStep={currentStep}
          answers={answers}
        />
      </div>
    );
  }

  // Check survey status (bypass in dev mode)
  if (!isDevelopment() && SURVEY_CONFIG.STATUS !== 'open') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
        <Header />
        <ClosedScreen status={SURVEY_CONFIG.STATUS} />
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return <SuccessScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
      <Header />
      <ProgressBar
        currentStep={currentStep}
        remainingQuestions={remainingQuestions}
        isEstimate={isEstimate}
      />

      <main ref={questionRef} className="flex-1 max-w-4xl mx-auto w-full px-6 py-3 pb-32">
        {/* Hero section - only on first question */}
        {currentStep === 0 && (
          <div className="md:mb-4 mb-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-2xl p-8 shadow-xl">
            <h2 className="text-xl md:text-2xl font-bold mb-4 text-center">
              Retrato dos Consumidores de Cerveja 2026
            </h2>
            <p className="text-base md:text-md leading-relaxed text-center">
              Cerveja é uma das paixões nacionais, e a nossa relação com ela muda continuamente. Agradecemos por dedicar alguns minutos para nos ajudar a entender seus hábitos de consumo, e toda a diversidade existente no mundo dos bebedores e bebedoras de cervejas.
            </p>
          </div>
        )}

        {/* Submission Error Message */}
        {submissionError && (
          <div className="mb-6 bg-red-50 border-2 border-red-500 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Erro ao enviar pesquisa
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{submissionError}</p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setSubmissionError(null)}
                    className="text-sm font-medium text-red-800 hover:text-red-900"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-500 rounded-lg p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mr-3"></div>
              <div className="text-amber-700 font-medium">
                Enviando sua pesquisa...
              </div>
            </div>
          </div>
        )}
        <QuestionCard
          question={currentQuestion}
          value={answers[currentQuestion.id]}
          error={error}
          onChange={handleAnswer}
          allAnswers={answers}
          navigationProps={{
            currentStep: navigationHistory.length - 1,
            totalSteps: QUESTIONS.length,
            isLastQuestion,
            onBack: handleBack,
            onNext: handleNext,
            onSubmit: handleSubmit,
            isSubmitting // Pass loading state to disable buttons
          }}
        />
      </main>

      <Footer />

      {/* Dev Tools - only shows in development */}
      <DevTools
        onFillMockData={handleFillMockData}
        onReset={handleReset}
        onJumpToQuestion={handleJumpToQuestion}
        onShowSuccess={handleShowSuccess}
        onShowClosedScreen={handleShowClosedScreen}
        onShowWaitingScreen={handleShowWaitingScreen}
        onBackToSurvey={handleBackToSurvey}
        questions={QUESTIONS}
        currentStep={currentStep}
        answers={answers}
      />
    </div>
  );
}
